import { FuzzyMatch } from '../utils/fuzzySearch';

/**
 * Filter — aplica filtros do preset/overrides nas rows produzidas pelo Selector.
 * Recebe Row[] (shape estrito do contract.js), retorna Row[] filtradas.
 *
 * Pure. Sem side effects. Sem leitura de globais. Sem cálculo derivado —
 * só leitura de campos JÁ pre-computados no row.
 */

/**
 * @param {import('./contract').Row[]} rows
 * @param {Object} preset                  preset ativo (define filters base)
 * @param {Object} overrides               filters do toolbar (sobrescreve preset)
 * @returns {import('./contract').Row[]}
 */
export function ApplyFilters(rows, preset, overrides) {
    const f = { ...(preset?.filters || {}), ...(overrides || {}) };
    let result = [...rows];

    // Multi-brand (preset) ∩ single-brand (override) — preset define o conjunto,
    // override afunila.
    if (Array.isArray(f.brands) && f.brands.length > 0) {
        const set = new Set(f.brands);
        result = result.filter((r) => set.has(r.brand));
    }
    if (f.brand) {
        result = result.filter((r) => r.brand === f.brand);
    }

    // Filtro de tags cobre as tags gerais (row.tags) E a coluna "Análise Full"
    // (row.fullTags) — o usuário filtra "full urgente" sem saber onde mora.
    const tagsOf = (r) => [...(Array.isArray(r.tags) ? r.tags : []), ...(Array.isArray(r.fullTags) ? r.fullTags : [])];
    if (Array.isArray(f.tags) && f.tags.length > 0) {
        const targets = f.tags.map((t) => String(t).toLowerCase());
        const matchAll = f.tags_match_all === true;
        result = result.filter((r) => {
            const own = tagsOf(r);
            if (own.length === 0) return false;
            return matchAll ? targets.every((t) => own.includes(t)) : targets.some((t) => own.includes(t));
        });
    }
    if (f.tag) {
        const target = String(f.tag).toLowerCase();
        result = result.filter((r) => tagsOf(r).includes(target));
    }

    if (Array.isArray(f.stock_rules) && f.stock_rules.length > 0) {
        for (const rule of f.stock_rules) {
            if (!rule || typeof rule.value !== 'number') continue;
            const op = rule.op === 'gt' ? 'gt' : 'lt';
            const cmp = (qty) => (op === 'gt' ? qty > rule.value : qty < rule.value);
            if (rule.provider === 'bling') {
                const depots = Array.isArray(rule.depots) ? rule.depots.map(String) : [];
                const depotSet = new Set(depots);
                result = result.filter((r) => {
                    if (depots.length === 0) return cmp(Number(r.bling || 0));
                    const qty = (r.depots || [])
                        .filter((d) => depotSet.has(String(d.id)))
                        .reduce((s, d) => s + Number(d.qty || 0), 0);
                    return cmp(qty);
                });
            } else if (rule.provider === 'ml') {
                const measures = Array.isArray(rule.measures) ? rule.measures : [rule.measure || 'avail'];
                result = result.filter((r) => {
                    let qty = 0;
                    if (measures.length === 0 || measures.includes('total')) {
                        qty = Number(r.mlTotal || 0);
                    } else {
                        if (measures.includes('avail')) qty += Number(r.mlAvail || 0);
                        if (measures.includes('unavail')) qty += Number(r.mlUnavail || 0);
                    }
                    return cmp(qty);
                });
            }
        }
    }

    if (f.search) {
        // Fuzzy match (substring + subsequence + token split) sobre os campos
        // do cadastro. Antes era `.toLowerCase().includes(q)` que falhava se
        // faltasse 1 char ou tivesse acento. Sintoma reportado:
        // "ENVELOPECOPA2026" não achava se digitasse "EVELOPECOPA".
        result = result.filter((r) =>
            FuzzyMatch(f.search, [
                r.name,
                r.sku,
                r.skus,
                r.inventoryId,
                r.mlb,
                r.brand,
                r.supplier_code,
                r.bling_id,
                r.mlbs,
                r.tags,
                r.fullTags,
            ]),
        );
    }

    return result;
}
